import { Component, Input, Output, EventEmitter  } from '@angular/core';
import { OCPPChargepoint } from '../../../api/chargepoints-api.service';
import { CommonModule } from '@angular/common';
import { ChargepointCardComponent } from '../../../components/chargepoints/chargepoint-card/chargepoint-card.component';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { ChargepointConfigurationOcppComponent } from '../../../components/chargepoint-configuration/chargepoint-configuration-ocpp/chargepoint-configuration-ocpp.component';
import { MatDividerModule } from '@angular/material/divider';
import { ChargepointStatusComponent } from '../../../components/chargepoints/chargepoint-status/chargepoint-status.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChargepointsOcppApiService } from '../../../api/chargepoints-ocpp-api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TransactionsListComponent } from '../../../components/transactions/transactions-list/transactions-list.component';
import { JsonViewerService } from '../../../components/common/json-viewer-dialog/json-viewer.service';
import { TransactionApiService } from '../../../api/transactions-api.service';
import { TransactionMonitorDialogService } from '../../../components/transactions/transaction-monitor-dialog/transaction-monitor-dialog.service';
import { ChargepointDiagnosticsComponent } from '../../../components/chargepoint-diagnostics/chargepoint-diagnostics.component';
import { MatDialog } from '@angular/material/dialog';
import { ChargepointTracesDialogComponent } from '../../../components/chargepoint-traces/chargepoint-traces-dialog/chargepoint-traces-dialog.component';
import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { ReplaySubject, Subject } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { SessionsHistoryComponent } from '../../../components/sessions/sessions-history/sessions-history.component';
import { ChargepointLocalAuthorizationListOcppComponent } from '../../../components/chargepoint-local-authorization-list/chargepoint-local-authorization-list-ocpp/chargepoint-local-authorization-list-ocpp.component';
@Component({
  selector: 'app-ocpp-chargepoint-page',
  templateUrl: './ocpp-chargepoint-page.component.html',
  styleUrls: ['./ocpp-chargepoint-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ChargepointCardComponent,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    ChargepointConfigurationOcppComponent,
    MatDividerModule,
    ChargepointStatusComponent,
    MatTooltipModule,
    TransactionsListComponent,
    ChargepointDiagnosticsComponent,
    MatMenuModule,
    SessionsHistoryComponent,
    ChargepointLocalAuthorizationListOcppComponent,
  ]
})
export class OcppChargepointPageComponent {

  @Input()
  chargepoint!: OCPPChargepoint;

  @Output()
  refresh = new EventEmitter<void>();

  public readonly localAuthListStatusConfig = {
    notSupported: { icon: 'block', label: 'Not supported' },
    disabled: { icon: 'power_off', label: 'Disabled' },
    synchronized: { icon: 'check_circle', label: 'Synchronized' },
  } as const;

  public refreshConfigurationTrigger$: Subject<void> = new ReplaySubject<void>(1);

  constructor(
    private chargepointsOcppApiService: ChargepointsOcppApiService,
    private transactionApiService: TransactionApiService,
    private transactionMonitorDialogService: TransactionMonitorDialogService,
    private snackBar: MatSnackBar,
    public jsonViewerService: JsonViewerService,
    private dialog: MatDialog
  ) {}

  public get isLocalAuthorizationListSupported(): boolean {
    return this.chargepoint?.localAuthorizationList?.supported === true;
  }

  public get localAuthorizationListStatus(): keyof typeof this.localAuthListStatusConfig | null {
    const list = this.chargepoint?.localAuthorizationList;
    if (!list?.supported) {
      return 'notSupported';
    }
    if (!list.enabled) {
      return 'disabled';
    }
    if (!list.synchronizationDone) {
      return null;
    }
    return 'synchronized';
  }

  public get localAuthorizationListStatusClass(): string | null {
    if (!this.localAuthorizationListStatus) {
      return null;
    }
    return this.localAuthorizationListStatus === 'notSupported'
      ? 'not-supported'
      : this.localAuthorizationListStatus;
  }

  public startTransaction(connectorId: number) {
    if (this.chargepoint) {
      this.transactionApiService.startTransaction({
        chargepointId: this.chargepoint.id,
        connectorId: connectorId,
        type: this.chargepoint.type
      }).subscribe((transaction) => {
        this.snackBar.open('Transaction started', 'Close', { duration: 3000 });
        this.transactionMonitorDialogService.open(transaction);
        this.refresh.emit();
      });
    }
  }

  public unlockConnector(connectorId: number) {
    if (this.chargepoint) {
      this.chargepointsOcppApiService.unlockConnector(this.chargepoint.id, connectorId).subscribe((unlockingStatus) => {
        switch(unlockingStatus) {
          case 'Unlocked':
            this.snackBar.open('Connector unlocked', 'Close', { duration: 3000 });
            break;
            case 'UnlockFailed':
              this.snackBar.open('Unlock failed', 'Close', { duration: 3000 });
              break;
            case 'NotSupported':
              this.snackBar.open('Not supported !', 'Close', { duration: 3000 });
              break;
          default:
            this.snackBar.open('Failed to unlock connector', 'Close', { duration: 3000 });
            break;
        }
        this.refresh.emit();
      });
    } 
  }

  public requestConnectorStatus(connectorId: number) {
    if (this.chargepoint) {
      this.chargepointsOcppApiService.requestConnectorStatus(this.chargepoint.id, connectorId).subscribe((status) => {
        switch (status) {
          case 'Accepted':
            this.snackBar.open('Status notification requested', 'Close', { duration: 3000 });
            break;
          case 'Rejected':
            this.snackBar.open('Status notification rejected', 'Close', { duration: 3000 });
            break;
          case 'NotImplemented':
            this.snackBar.open('Status notification not supported', 'Close', { duration: 3000 });
            break;
          default:
            this.snackBar.open('Failed to request status', 'Close', { duration: 3000 });
            break;
        }
        this.refresh.emit();
      });
    }
  }

  public requestDiagnostic() {
    this.confirmAction({
      title: 'Request Diagnostic',
      content: 'Are you sure you want to request a diagnostic?',
      confirmLabel: 'Request',
    }, () => {
      if (this.chargepoint) {
        this.chargepointsOcppApiService.requestDiagnostic(this.chargepoint.id).subscribe(() => {
          this.snackBar.open('Diagnostic requested', 'Close', { duration: 3000 });
          this.refresh.emit();
        });
      }
    });
  }

  public requestClearCache() {
    this.confirmAction({
      title: 'Clear Cache',
      content: 'Are you sure you want to clear the cache?',
      confirmLabel: 'Clear',
    }, () => {
      if (this.chargepoint) {
        this.chargepointsOcppApiService.clearCache(this.chargepoint.id).subscribe(() => {
          this.snackBar.open('Cache cleared', 'Close', { duration: 3000 });
          this.refresh.emit();
        });
      }
    });
  }

  public requestReboot(hard: boolean) {
    this.confirmAction({
      title: 'Reboot',
      content: 'Are you sure you want to reboot the chargepoint?',
      confirmLabel: 'Reboot',
    }, () => {
      if (this.chargepoint) {
        this.chargepointsOcppApiService.reboot(this.chargepoint.id, hard).subscribe(() => {
          this.snackBar.open('Reboot requested', 'Close', { duration: 3000 });
          this.refresh.emit();
        });
      }
    });
  }

  public changeAvailabilityToOperative() {
    this.confirmAction({
      title: 'Make operative',
      content: 'Are you sure you want to make the chargepoint operative?',
      confirmLabel: 'Make operative',
    }, () => {
      if (this.chargepoint) {
        this.chargepointsOcppApiService.changeAvailability(this.chargepoint.id, true).subscribe((status: string) => {
              switch(status.toLowerCase()) {
                case 'accepted':
                  this.snackBar.open('Chargepoint made operative', 'Close', { duration: 3000 });
                  break;
                case 'scheduled':
                  this.snackBar.open('Chargepoint scheduled to be operative', 'Close', { duration: 3000 });
                  break;
                case 'rejected':
                default:
                  this.snackBar.open('Failed to make chargepoint operative', 'Close', { duration: 3000 });
          }
          this.refresh.emit();
        });
      }
    });
  }

  private confirmAction(dialogProps: {title: string, content: string, confirmLabel?: string, cancelLabel?: string}, action: () => void) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogProps
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        action();
      }
    });
  }

  public showLogsDialog() {
    this.dialog.open(ChargepointTracesDialogComponent, {
      data: { chargepoint: this.chargepoint },
      width: '850px',
      maxWidth: '850px'
    });
  }
}
